import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import api from '../../lib/api'
import { StoryStatusType, StoryType } from '../../lib/types'
import { SocketConnectorContext } from '../SocketConnector'

const emptyStatus = (storyId = ''): StoryStatusType => ({
  storyId,
  processingGraphics: false,
  processingTranscript: false,
  processingSubtitle: false,
  processingMedia: false,
  mediaReady: false,
  mediaError: false,
  thumbnailReady: false
})

const statusFromStory = (story?: StoryType): StoryStatusType => {
  if (!story) {
    return emptyStatus()
  }

  return {
    storyId: story.id,
    processingGraphics: story.processingGraphics ?? false,
    processingTranscript: story.processingTranscript ?? false,
    processingSubtitle: story.processingSubtitle ?? false,
    processingMedia: false,
    mediaReady: !!(story.ready && story.url),
    mediaError: story.error ?? false,
    mediaStatus: story.ready ? 'SUCCESS' : story.error ? 'ERROR' : undefined,
    thumbnailReady: story.type !== 'VIDEO' || !!story.thumbnailUrl,
    mergeStatus: story.mergeStatus,
    graphicsProcessed: story.graphicsProcessed,
    sentToDam: story.sentToDam,
    graphicsFailureReason: story.graphicsFailureReason,
    graphicsLastAttempt: story.graphicsLastAttempt,
    graphicsAttemptCount: story.graphicsAttemptCount,
    transcriptFailureReason: story.transcriptFailureReason,
    transcriptLastAttempt: story.transcriptLastAttempt,
    transcriptAttemptCount: story.transcriptAttemptCount
  }
}

const hasActiveProcessing = (status?: StoryStatusType) =>
  !!status &&
  (status.processingGraphics || status.processingTranscript || status.processingSubtitle || status.processingMedia)

export const useStoryProcessingStatus = (story: StoryType | undefined, reload: () => void) => {
  const { client: socketClient, connected } = useContext(SocketConnectorContext)
  const [processingStatus, setProcessingStatusState] = useState<StoryStatusType>(() => statusFromStory(story))
  const processingStatusRef = useRef<StoryStatusType>(processingStatus)

  const setProcessingStatus = useCallback(
    (status: StoryStatusType, reloadOnComplete = false) => {
      const previousStatus = processingStatusRef.current
      const wasProcessing = hasActiveProcessing(previousStatus)

      processingStatusRef.current = status
      setProcessingStatusState(status)

      if (reloadOnComplete && wasProcessing && !hasActiveProcessing(status)) {
        reload()
      }
    },
    [reload]
  )

  useEffect(() => {
    const status = statusFromStory(story)
    setProcessingStatus(status)
  }, [
    story?.id,
    story?.processingGraphics,
    story?.processingTranscript,
    story?.processingSubtitle,
    story?.ready,
    story?.error,
    story?.url,
    story?.thumbnailUrl,
    story?.mergeStatus,
    story?.graphicsFailureReason,
    story?.graphicsLastAttempt,
    story?.graphicsAttemptCount,
    story?.transcriptFailureReason,
    story?.transcriptLastAttempt,
    story?.transcriptAttemptCount,
    setProcessingStatus
  ])

  const applyRemoteStatus = useCallback(
    (status: StoryStatusType | undefined) => {
      if (!status || status.storyId !== story?.id) {
        return
      }

      setProcessingStatus(status, true)
    },
    [setProcessingStatus, story?.id]
  )

  useEffect(() => {
    if (!connected || !story?.id) {
      return
    }

    const subscription = socketClient.subscribe('/user/queue/story-processing-status', (message: { body: string }) => {
      try {
        const { payload } = JSON.parse(message.body)
        applyRemoteStatus(payload)
      } catch (error) {
        console.error('Error parsing story processing status message:', error)
      }
    })

    return () => subscription.unsubscribe()
  }, [applyRemoteStatus, connected, socketClient, story?.id])

  useEffect(() => {
    if (!connected || !story?.id) {
      return
    }

    let isMounted = true

    api
      .checkProcessing(story.id)
      .then(status => {
        if (isMounted) {
          applyRemoteStatus(status)
        }
      })
      .catch(error => {
        console.error('Error fetching story processing status snapshot:', error)
      })

    return () => {
      isMounted = false
    }
  }, [applyRemoteStatus, connected, story?.id])

  return {
    processingStatuses: processingStatus,
    storyStatus: processingStatus,
    graphicsStatus: processingStatus.mergeStatus,
    finishedProcessing: !hasActiveProcessing(processingStatus),
    setProcessingStatus,
    hasActiveProcessing
  }
}
